import React from "react"; // 导入React
import { Navigate, useLocation, useNavigate, Outlet } from "react-router-dom"; // 导入路由导航组件
import {
  ApartmentOutlined,
  FileTextOutlined,
  TagsOutlined,
  DashboardOutlined,
  ApiOutlined,
} from "@ant-design/icons"; // 导入图标
import { MainLayout, LayoutConfig } from "@ms/shared-components"; // 直接导入共享布局组件
import { AuthGuardWithConfig } from "@ms/shared-auth"; // 导入认证保护组件
import { msSpaceAuthConfig } from "./config"; // 导入认证配置

// 个人资料页面
import SpaceList from "./pages/list/index"; // 导入空间列表组件
import SpaceTree from "./pages/tree/index"; // 导入空间树组件
import SpaceVisual from "./pages/visual/index"; // 导入空间可视化组件
import TagManagement from "./pages/tags/index"; // 导入标签管理组件
import ApiDocsPage from "./pages/apiDocs/index"; // 导入接口描述组件

// 调试工具组件已移除

// 移除所有认证相关功能

// 移除所有认证相关导入

// 移除所有认证检查函数

// 移除所有认证相关的测试函数

// 主布局组件
const MainLayoutComponent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const menuItems = getMenuItems();

  // 将路由菜单项转换为共享组件需要的格式
  const convertMenuItems = (routes: any[]): any[] => {
    return routes.map((route) => {
      const fullPath = route.path ? `/${route.path}` : route.path; // 确保路径以 / 开头

      return {
        key: route.key,
        icon: route.icon,
        label: route.label,
        path: fullPath,
        onClick: () => {
          if (fullPath) {
            try {
              if (!navigate || typeof navigate !== "function") {
                window.location.href = fullPath;
                return;
              }

              navigate(fullPath, { replace: false });

              // 检查 navigate 是否成功更新了 location
              // 如果 200ms 后 location 还没有更新，说明 navigate 没有正常工作
              setTimeout(() => {
                const currentPath = window.location.pathname;
                if (
                  currentPath === fullPath &&
                  location.pathname !== fullPath
                ) {
                  // 强制刷新页面以确保路由正确
                  window.location.reload();
                }
              }, 200);
            } catch (error) {
              console.error("[MainLayoutComponent] navigate 调用失败:", error);
              // 如果 navigate 失败，使用 window.location 作为 fallback
              window.location.href = fullPath;
            }
          }
        },
      };
    });
  };

  // 根据当前路径生成面包屑
  const generateBreadcrumbs = () => {
    const path = location.pathname;
    const breadcrumbs = [
      {
        title: "首页",
        href: "/",
      },
    ];

    // 从菜单项中找到当前路径对应的项
    const currentMenuItem = menuItems.find((item) => {
      const fullPath = item.path ? `/${item.path}` : item.path;
      return fullPath === path;
    });

    if (currentMenuItem) {
      breadcrumbs.push({
        title: currentMenuItem.label as string,
        href: currentMenuItem.path
          ? `/${currentMenuItem.path}`
          : currentMenuItem.path || "",
      });
    }

    return breadcrumbs;
  };

  // 创建布局配置
  const layoutConfig: LayoutConfig = {
    system: {
      name: "空间管理系统",
      shortName: "空间管理",
    },
    menuItems: convertMenuItems(menuItems),
    breadcrumbs: generateBreadcrumbs(),
    headerActions: [],
    styles: {
      siderBackground: "#f0f1f2",
      headerBackground: "#fff",
      contentBackground: "#fff",
      primaryColor: "#1890ff",
    },
  };

  return (
    <>
      <MainLayout config={layoutConfig}>
        <Outlet />
      </MainLayout>
    </>
  );
};

// 简化的getRouteObjects函数
// 注意：React Router v6 要求路由配置中的 element 必须是 React 元素
// 但是 routes 数组在模块加载时就创建了，所以每次调用都返回相同的引用
// 清理路由配置，移除非标准字段（key, label, icon），只保留 React Router 需要的字段
// 注意：必须保留 path, element, children 等 React Router 需要的字段
const cleanRouteConfig = (route: any): any => {
  // 只移除非标准字段，保留所有 React Router 需要的字段
  const cleaned: any = {
    path: route.path,
    element: route.element,
  };

  // 如果有 children，递归清理
  if (route.children && Array.isArray(route.children)) {
    cleaned.children = route.children.map(cleanRouteConfig);
  }

  // 保留 index 字段（如果有）
  if (route.index !== undefined) {
    cleaned.index = route.index;
  }

  // 保留 caseSensitive 字段（如果有）
  if (route.caseSensitive !== undefined) {
    cleaned.caseSensitive = route.caseSensitive;
  }

  return cleaned;
};

export const getRouteObjects = (): any[] => {
  // 清理路由配置，移除非标准字段
  const cleanedRoutes = routes.map(cleanRouteConfig);
  return cleanedRoutes;
};

// 自定义路由对象类型
interface RouteObjectType {
  path?: string;
  element?: React.ReactNode;
  index?: boolean;
  children?: RouteObjectType[];
  caseSensitive?: boolean;
}

/**
 * 路由配置类型
 */
export interface AppRoute extends RouteObjectType {
  key?: string; // 路由的唯一标识
  label?: string; // 菜单显示名称
  icon?: React.ReactNode; // 菜单图标
  hideInMenu?: boolean; // 是否在菜单中隐藏
  children?: AppRoute[]; // 子路由
}

/**
 * 应用路由配置
 */
export const routes: AppRoute[] = [
  // 主布局路由，包含所有页面
  {
    path: "/",
    element: <MainLayoutComponent />,
    key: "root",
    children: [
      // 空间列表
      {
        path: "list",
        element: (
          <AuthGuardWithConfig
            config={msSpaceAuthConfig}
            requiredPermissions={["msspace:space:view"]}
            fallbackMessage="您没有访问空间列表页面的权限"
          >
            <SpaceList />
          </AuthGuardWithConfig>
        ),
        key: "space.list",
        label: "空间列表",
        icon: <FileTextOutlined />,
      },

      // 空间结构
      {
        path: "tree",
        element: (
          <AuthGuardWithConfig
            config={msSpaceAuthConfig}
            requiredPermissions={["msspace:space:view"]}
            fallbackMessage="您没有访问空间树形页面的权限"
          >
            <SpaceTree />
          </AuthGuardWithConfig>
        ),
        key: "space.tree",
        label: "空间结构",
        icon: <ApartmentOutlined />,
      },

      // 标签管理
      {
        path: "tags",
        element: (
          <AuthGuardWithConfig
            config={msSpaceAuthConfig}
            requiredPermissions={["msspace:tags:view"]}
            fallbackMessage="您没有访问标签管理页面的权限"
          >
            <TagManagement />
          </AuthGuardWithConfig>
        ),
        key: "space.tags",
        label: "标签管理",
        icon: <TagsOutlined />,
      },

      // 空间可视化
      {
        path: "visual",
        element: (
          <AuthGuardWithConfig
            config={msSpaceAuthConfig}
            requiredPermissions={["msspace:visual:view"]}
            fallbackMessage="您没有访问空间可视化页面的权限"
          >
            <SpaceVisual />
          </AuthGuardWithConfig>
        ),
        key: "space.visual",
        label: "空间可视化",
        icon: <DashboardOutlined />,
      },

      // 接口描述
      {
        path: "api-docs",
        element: <ApiDocsPage />,
        key: "space.api-docs",
        label: "接口描述",
        icon: <ApiOutlined />,
      },

      // 移除个人中心页面

      // 默认路由（根路径重定向到空间结构）
      {
        path: "",
        element: <Navigate to="/list" replace />,
        key: "home",
        hideInMenu: true,
      },

      // 404路由（未匹配的路径重定向到空间结构）
      {
        path: "*",
        element: <Navigate to="/tree" replace />,
        key: "404",
        hideInMenu: true,
      },
    ],
  },
];

/**
 * 将路由配置转换为React Router所需的路由对象
 * 确保这个函数被正确导出
 */

/**
 * 获取菜单项配置
 * 从路由配置中筛选出需要在菜单中显示的项目
 */
export const getMenuItems = () => {
  // 找到主布局路由，获取其子路由作为菜单项
  const rootRoute = routes.find((route) => route.path === "/");
  if (rootRoute && rootRoute.children) {
    return rootRoute.children.filter((route) => !route.hideInMenu);
  }
  return [];
};

export default routes;
